import { DetailsPanelSource, useDetailsPanelsActions } from "PFApp/booking/components/details_panel";
import { activityTypeToSidePanelTypeMap } from "PFApp/booking/components/details_panel/details_panel.types";
import { useCanViewActivityInBM } from "PFApp/booking/hooks/use_can_view_activity_in_bm";
import detailViewCss from "PFApp/booking/parts/overview/detail_view/detail_view.module.scss";
import { ActionType } from "PFApp/booking/query_params_handler.utils";
import { useActivityLink, useTemplate } from "PFApp/hooks";
import { Button } from "PFComponents/button";
import { Inline } from "PFComponents/containers";
import { LoadingDots } from "PFComponents/loading_dots";
import { Typography } from "PFComponents/typography";
import { Activity } from "PFTypes";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { BookingDetailItem, BookingDetailItemAction } from "../booking_detail_item";
import { EntityIcon } from "../entity_icon";
import css from "./booking_detail_activity.module.scss";

type BookingDetailsActivityProps = {
  activity?: Activity;
  parentActivity?: Activity;
  isLoading?: boolean;
  source?: DetailsPanelSource;
};

export const BookingDetailActivity = ({
  activity,
  parentActivity,
  isLoading,
  source
}: BookingDetailsActivityProps): React.ReactElement | null => {
  const { t } = useTranslation(["activities", "bookingModule"]);
  const navigate = useNavigate();
  const { openDetailsPanel } = useDetailsPanelsActions();

  const { template_id: templateId } = activity || {};
  const { key: templateKey } = useTemplate(templateId) || {};

  const linkUrl = useActivityLink({ activity, parentActivity });
  const canViewInBookingModule = useCanViewActivityInBM({ activity });

  const openActivitySidePanel = () => {
    activity?.id &&
      templateKey &&
      openDetailsPanel(
        {
          id: activity.id,
          type: activityTypeToSidePanelTypeMap[templateKey],
          source
        },
        true
      );
  };

  if (!activity) {
    return null;
  }

  if (isLoading) {
    return <LoadingDots />;
  }

  const showInProjectsTab = {
    icon: source === DetailsPanelSource.BookingModuleWorkforce ? ("folder" as const) : ("booking" as const),
    onClick: () => {
      const params = new URLSearchParams({
        action: String(ActionType.ShowInTab),
        mode: "projects",
        activityId: String(activity.id)
      });
      navigate({ pathname: "/booking/overview", search: params.toString() }, { replace: true });
    },
    tooltip: t("bookingModule:rowsSidePanel.rowMenu.showInTab.workforce")
  };

  const showInWorkforceTab = {
    icon: "profiles" as const,
    onClick: () => {
      const params = new URLSearchParams({
        action: String(ActionType.ShowInTab),
        mode: "workforce",
        activityId: String(activity.id)
      });
      navigate({ pathname: "/booking/overview", search: params.toString() }, { replace: true });
    },
    tooltip: t("bookingModule:rowsSidePanel.rowMenu.showInTab.projects")
  };

  const viewInBMAction =
    source === DetailsPanelSource.BookingModuleProjects ? showInWorkforceTab : showInProjectsTab;
  const actions: BookingDetailItemAction[] = [
    ...(canViewInBookingModule ? [viewInBMAction] : []),
    ...(linkUrl
      ? [
          {
            icon: "open" as const,
            onClick: () => linkUrl && navigate(linkUrl),
            tooltip: t(`bookingModule:details.openPage.${templateKey}` as unknown as TemplateStringsArray)
          }
        ]
      : [])
  ];

  return (
    <div className={detailViewCss.actionableContainer}>
      <BookingDetailItem
        label={t(`activities:templates.${templateKey}` as unknown as TemplateStringsArray)}
        value={
          <Button kind="text" className={css.activityName} onClick={openActivitySidePanel}>
            <Inline>
              <EntityIcon templateId={templateId} type={templateKey || ""} color="palettePrimary0" />
              <Typography variant="bodyBold">{activity.name}</Typography>
            </Inline>
          </Button>
        }
        actions={actions}
      />
    </div>
  );
};
